/**
 * Shared home KPI snapshot helpers — same pattern as AI chat (FE already has BQ data;
 * consumers reuse it; no extra BigQuery round-trip).
 *
 * sessionStorage keeps last snapshot so Settings/GoalEdit after refresh
 * can paint instantly while BQ revalidates in the background.
 *
 * AI-tab dossier (`buildChatDossier`) packs 当月 + 目標・注力 + 履歴 + 予測 into the
 * existing `kpi_summary` string — no BQ tools on chat.
 */

import { isYmComplete, jstMonth, jstToday, lastCompleteYm, previousYm } from './jstDate';
import {
  KPI_BY_FOCUS_KEY,
  SCORECARD_ENTRIES,
  deriveCustomerCount,
  deriveTechSales,
  deriveUnitPrice,
  readKpiValue,
} from './kpiRegistry';

// v2 (2026-09-29, main-store task T3): a registered person's numbers now cover all their
// stores — never paint a snapshot saved under the old per-store rule.
// Every snapshot key carries the build id (Dockerfile sets REACT_APP_BUILD_ID per image), so a
// new app version never paints a snapshot written by the previous one: sessionStorage survives
// the PWA update reload, and a changed response shape must not reach the new code's first paint.
// Local dev has no build id → 'dev'.
const BUILD = process.env.REACT_APP_BUILD_ID || 'dev';
export const KPI_CACHE_PREFIX = `kuroco_kpi_snap_v2_${BUILD}_`;
const HIST_CACHE_PREFIX = `kuroco_kpi_hist_v2_${BUILD}_`;

/** Default months of 実績 in AI dossier (D1 still open; 18 matches buildSalesHistorySummary). */
export const CHAT_DOSSIER_HISTORY_LIMIT = 18;

export function filterKey(store, stylist, month) {
  return `${store || ''}|${stylist || ''}|${month || ''}`;
}

function readJson(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeJson(key, data) {
  try {
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch {
    /* quota / private mode */
  }
}

/**
 * A snapshot is only good for the JST day it was taken.
 * The ETL lands new numbers every morning, so a cache written yesterday would
 * paint stale figures before the revalidation returns — `savedAt` was recorded
 * for exactly this and never read.
 */
function isFromToday(savedAt) {
  if (typeof savedAt !== 'number' || !Number.isFinite(savedAt)) return false;
  return jstToday(new Date(savedAt)) === jstToday();
}

/** @returns {{ kpis: object, key: string } | null} */
export function readCachedKpis(store, stylist, month) {
  const key = filterKey(store, stylist, month);
  if (!store || !stylist || !month) return null;
  const cached = readJson(KPI_CACHE_PREFIX + key);
  if (!cached?.kpis || typeof cached.kpis !== 'object') return null;
  if (!isFromToday(cached.savedAt)) return null;
  return { kpis: cached.kpis, key };
}

export function writeCachedKpis(store, stylist, month, kpis) {
  if (!store || !stylist || !month || !kpis) return;
  writeJson(KPI_CACHE_PREFIX + filterKey(store, stylist, month), {
    kpis,
    savedAt: Date.now(),
  });
}

/** @returns {{ history: array, key: string } | null} */
export function readCachedKpiHistory(store, stylist) {
  const key = `${store || ''}|${stylist || ''}`;
  if (!store || !stylist) return null;
  const cached = readJson(HIST_CACHE_PREFIX + key);
  if (!Array.isArray(cached?.history)) return null;
  if (!isFromToday(cached.savedAt)) return null;
  return { history: cached.history, key };
}

export function writeCachedKpiHistory(store, stylist, history) {
  if (!store || !stylist || !Array.isArray(history)) return;
  writeJson(HIST_CACHE_PREFIX + `${store}|${stylist}`, {
    history,
    savedAt: Date.now(),
  });
}

/**
 * Generic day-scoped snapshot (stale-while-revalidate, audit #9 2026-10-18) for the rest of
 * what the home screen needs: forecasts, store average, AI comment, and the picker lists.
 * Same rule as the KPI snapshot above — good only for the JST day it was written, because
 * the ETL lands new numbers every morning. sessionStorage: per tab, gone when the tab closes.
 */
const SNAP_PREFIX = `kuroco_snap_v1_${BUILD}_`;

export function readDaySnapshot(name, key) {
  if (!name || key == null) return null;
  const cached = readJson(`${SNAP_PREFIX}${name}|${key}`);
  if (!cached || !('data' in cached) || !isFromToday(cached.savedAt)) return null;
  return cached.data;
}

export function writeDaySnapshot(name, key, data) {
  if (!name || key == null || data === undefined) return;
  writeJson(`${SNAP_PREFIX}${name}|${key}`, { data, savedAt: Date.now() });
}

const UNIT_SUFFIX = { percent: '%', days: '日', pieces: '枚', count: '' };

function formatRegistryValue(entry, v) {
  if (typeof v !== 'number' || Number.isNaN(v)) return '未取得';
  if (entry.unit === 'currency') return `¥${Math.round(v).toLocaleString('ja-JP')}`;
  const n = entry.unit === 'percent' || entry.unit === 'days' ? Math.round(v * 10) / 10 : Math.round(v);
  return `${n}${UNIT_SUFFIX[entry.unit] ?? ''}`;
}

/**
 * Every KPI the Home scorecard shows, read the same way the cards read it (registry fields +
 * derivations). Generated from SCORECARD_ENTRIES so the dossier can never lag behind Home again
 * (技術売上 was missing until 2026-10-26 and the AI answered with 売上総額 instead).
 */
export function buildScorecardLine(kpis) {
  if (!kpis || typeof kpis !== 'object') return '';
  const parts = SCORECARD_ENTRIES.map((e) => `${e.label} ${formatRegistryValue(e, readKpiValue(e, kpis))}`);
  return `全指標（ホームのカードと同じ）: ${parts.join(' / ')}`;
}

/** Format dashboard kpis for AI session / debug (matches AiStack). */
export function buildKpiSummary(kpis, selectedMonth) {
  if (!kpis || typeof kpis !== 'object') return '';
  const yen = (v) => (typeof v === 'number' ? `¥${Math.round(v).toLocaleString('ja-JP')}` : '未取得');
  const num = (v, suffix = '') => (typeof v === 'number' ? `${v}${suffix}` : '未取得');
  const month = selectedMonth || '';
  const customers =
    typeof kpis.customer_count === 'number'
      ? kpis.customer_count
      : typeof kpis.total_customers === 'number'
        ? kpis.total_customers
        : typeof kpis.new_customer_count === 'number' && typeof kpis.repeat_customer_count === 'number'
          ? kpis.new_customer_count + kpis.repeat_customer_count
          : null;
  const unitPrice =
    typeof kpis.avg_customer_spend === 'number'
      ? kpis.avg_customer_spend
      : typeof kpis.total_sales === 'number' && typeof kpis.customer_count === 'number' && kpis.customer_count > 0
        ? Math.round(kpis.total_sales / kpis.customer_count)
        : null;
  const nrr = kpis.new_return_rate ?? kpis.new_customer_return_rate;
  // 技術売上 is its own metric (registry: column, else 売上 − 店販). Without it on the line the
  // model answered a 技術売上 question with 売上総額 (猪口さん 10/25, task 2026-10-26 D).
  const techSales = deriveTechSales(kpis);
  const techUnit =
    typeof techSales === 'number' && typeof customers === 'number' && customers > 0
      ? Math.round(techSales / customers)
      : null;
  // 会社プロファイルの metrics_order（①指名率→②リピート→③生産性、売上は結果）に合わせて
  // ドシエの並び順も揃える。AIが抽象的なルールより先に、渡されたデータの並びをそのまま
  // なぞって話してしまう問題への対処（2026-10-02 AI動作確認で発見）。
  return (
    `${month}: 指名率 ${num(kpis.nomination_rate, '%')} / 指名客数 ${num(kpis.nominated_customers, '名')}` +
    ` / 新規再来率 ${num(nrr, '%')}` +
    ` / 客単価 ${yen(unitPrice)}` +
    ` / 店販 ${yen(kpis.retail_sales)}` +
    ` / 技術売上 ${yen(techSales)} / 技術単価 ${yen(techUnit)}` +
    ` / 総客数 ${num(customers, '名')}` +
    `（新規${num(kpis.new_customer_count ?? kpis.new_customers)}・リピート${num(kpis.repeat_customer_count ?? kpis.repeat_customers)}）` +
    ` / 売上 ${yen(kpis.total_sales)}` +
    `\n${buildScorecardLine(kpis)}`
  );
}

/**
 * Monthly series for goals consult. 売上 is always there — it is the KGI itself — and the
 * remaining columns follow the 注力 keys actually in play for this stylist.
 *
 * They used to be hardwired to 客単価 / 新規再来率 for everyone, which meant a stylist whose
 * focus was 指名率 or 店販 got a history of two KPIs nobody was working on, and none of the
 * one they were.
 */
export function buildSalesHistorySummary(kpiHistory, limit = 18, focusKeys = []) {
  if (!Array.isArray(kpiHistory) || kpiHistory.length === 0) return '月次売上履歴: 未取得';
  const rows = kpiHistory.slice(-limit);
  const cols = (Array.isArray(focusKeys) ? focusKeys : [])
    .filter(Boolean)
    .filter((k) => k !== 'totalSales')
    .map((k) => KPI_BY_FOCUS_KEY[k])
    .filter(Boolean);
  const shown = cols.length ? cols : [KPI_BY_FOCUS_KEY.unitPrice, KPI_BY_FOCUS_KEY.newReturn].filter(Boolean);

  const lines = rows.map((r) => {
    const m = r.month || r.ym || '?';
    const sales =
      typeof r.total_sales === 'number'
        ? `¥${Math.round(r.total_sales).toLocaleString('ja-JP')}`
        : '—';
    const rest = shown.map((entry) => {
      const v = readKpiValue(entry, r);
      if (typeof v !== 'number' || Number.isNaN(v)) return `${entry.label}—`;
      if (entry.unit === 'percent') return `${entry.label}${Math.round(v * 10) / 10}%`;
      if (entry.unit === 'currency') return `${entry.label}¥${Math.round(v).toLocaleString('ja-JP')}`;
      return `${entry.label}${Math.round(v)}`;
    });
    return [`${m}: 売上${sales}`, ...rest].join(' / ');
  });
  const what = shown.length ? `売上 + ${shown.map((e) => e.label).join('・')}` : '売上';
  return (
    `月次実績（新しい順に最大${rows.length}ヶ月・${what}・一貫性チェック用）:\n` + lines.join('\n')
  );
}

/**
 * kpi_summary for GoalEdit 【目標相談】— 当月 + history + 予測 + GAP + identities + draft.
 */
// Every 注力 lever (registry minus the KGI itself). 最高 is shown as context, not a save floor.
const RANGE_FOCUS_KEYS = Object.keys(KPI_BY_FOCUS_KEY).filter((k) => k !== 'totalSales');

/**
 * Achieved min/max per candidate KPI.
 *
 * The goal prompt forbids proposing a target below a level the stylist has already hit,
 * but the monthly line in this dossier only carries 売上 / 客単価 / 新規再来率 — for
 * 指名率, 客数, 店販 the model saw no history at all and so could not apply that rule.
 * It proposed 10% against a history that already contained 15.8%.
 */
/**
 * min / max / latest actually reached for one 注力 key. `max` is the level the stylist has
 * already demonstrated — a proposed target below it is a step backwards, not a goal.
 */
export function achievedRange(kpiHistory, focusKey, historyLimit = CHAT_DOSSIER_HISTORY_LIMIT) {
  const entry = KPI_BY_FOCUS_KEY[focusKey];
  if (!entry) return null;
  const rows = Array.isArray(kpiHistory)
    ? kpiHistory.slice(-Math.max(1, historyLimit || CHAT_DOSSIER_HISTORY_LIMIT))
    : [];
  const vals = rows
    .map((r) => readKpiValue(entry, r))
    .filter((v) => typeof v === 'number' && !Number.isNaN(v));
  if (vals.length === 0) return null;
  return {
    min: Math.min(...vals),
    max: Math.max(...vals),
    latest: vals[vals.length - 1],
    months: vals.length,
    unit: entry.unit,
    label: entry.label,
  };
}

export function buildFocusRangeSection(kpiHistory, historyLimit = CHAT_DOSSIER_HISTORY_LIMIT) {
  const rows = Array.isArray(kpiHistory)
    ? kpiHistory.slice(-Math.max(1, historyLimit || CHAT_DOSSIER_HISTORY_LIMIT))
    : [];
  if (rows.length === 0) return '【到達実績レンジ】未取得';
  const lines = [];
  for (const key of RANGE_FOCUS_KEYS) {
    const entry = KPI_BY_FOCUS_KEY[key];
    if (!entry) continue;
    const vals = rows
      .map((r) => readKpiValue(entry, r))
      .filter((v) => typeof v === 'number' && !Number.isNaN(v));
    if (vals.length === 0) continue;
    const fmt = (v) =>
      entry.unit === 'percent'
        ? `${Math.round(v * 10) / 10}%`
        : entry.unit === 'currency'
          ? yenOrDash(v)
          : String(Math.round(v));
    lines.push(
      `${entry.label}（key=${key}）: 最低 ${fmt(Math.min(...vals))}` +
        ` / 最高 ${fmt(Math.max(...vals))} / 直近 ${fmt(vals[vals.length - 1])}`
    );
  }
  if (lines.length === 0) return '【到達実績レンジ】未取得';
  return (
    `【到達実績レンジ】（直近${rows.length}ヶ月・参考。提案の下限は当月現状であり「最高」ではない）\n` +
    lines.join('\n')
  );
}

/** History row for the last calendar-complete month, or null if that month is missing. */
export function findLastCompleteHistoryRow(kpiHistory, now = new Date()) {
  const ym = lastCompleteYm(now);
  if (!ym || !Array.isArray(kpiHistory)) return { ym, row: null };
  const row = kpiHistory.find((r) => (r.month || r.ym) === ym) || null;
  return { ym, row };
}

/**
 * The month every 実績 on the goal screen is read from (picker chips, 現状, proposal floor,
 * gap ranking). A month that has ended is its own base. A month still running is MTD, not a
 * monthly figure, so the last complete month's history row is the base instead — the same base
 * the AI prompt is told to reason from. When that row is missing (ETL not yet run early in the
 * month) the MTD snapshot stays, flagged `mtd: true` so the screen can say so.
 * @returns {{ kpis: object|null, month: string|null, label: string, mtd: boolean }}
 */
export function resolveGoalBaseSnapshot(kpis, kpiHistory, selectedMonth, now = new Date()) {
  const monthLabel = (ym) => (ym ? `${Number(String(ym).slice(5, 7))}月実績` : '現状');
  if (!selectedMonth || isYmComplete(selectedMonth, now)) {
    return { kpis: kpis ?? null, month: selectedMonth ?? null, label: '現状', mtd: false };
  }
  const { ym, row } = findLastCompleteHistoryRow(kpiHistory, now);
  if (row && ym) return { kpis: row, month: ym, label: monthLabel(ym), mtd: false };
  return { kpis: kpis ?? null, month: selectedMonth, label: '現状（今月途中）', mtd: true };
}

/**
 * When the selected month has not ended (JST still in that month), tell the model
 * the card numbers are MTD and the last finished month is the calculation base.
 */
export function buildIncompleteMonthSection(kpiHistory, selectedMonth, now = new Date()) {
  if (!selectedMonth || isYmComplete(selectedMonth, now)) return null;
  const today = jstToday(now);
  const md = `${Number(today.slice(5, 7))}/${Number(today.slice(8, 10))}`;
  const { ym, row } = findLastCompleteHistoryRow(kpiHistory, now);
  const head =
    `【当月は未完了】${selectedMonth} は JST 本日 ${md} 時点の途中月` +
    `（月の最終日がまだ来ていない）。当月の率・人数・売上は途中集計であり月次の実力ではない。`;
  if (!ym) {
    return `${head}直近完了月を特定できない。当月を月次水準として年換算・月換算するな。`;
  }
  if (!row) {
    return (
      `${head}逆算・「このままだとこう」の基準は直近完了月 ${ym}（暦が次の月に入った最後の月）。` +
      `その月の履歴なし。当月の途中値を月次水準として扱わない。`
    );
  }
  return (
    `${head}逆算・配分・実力の基準は直近完了月 ${ym}（暦が次の月に入った最後の月）の実績:\n` +
    buildKpiSummary(row, ym) +
    '\n当月の途中値を月次水準として日数で年換算・月換算するな。'
  );
}

/** Distance from baseline sales to the saved goal — the number every target is calibrated against. */
export function buildGoalGapLine(kpis, draft, gapOpts = {}) {
  const src = gapOpts.baselineKpis || kpis;
  const label = gapOpts.baselineLabel || '当月売上';
  const cur = typeof src?.total_sales === 'number' ? src.total_sales : null;
  const goal = draft?.salesGoal != null ? Number(draft.salesGoal) : null;
  if (cur == null || goal == null || Number.isNaN(goal)) return null;
  const diff = goal - cur;
  const pct = cur > 0 ? Math.round((diff / cur) * 1000) / 10 : null;
  const span = draft?.planMonths != null ? `${draft.planMonths}ヶ月` : '未設定（6ヶ月として扱う）';
  return (
    `【目標差】${label} ${yenOrDash(cur)} → ゴール ${yenOrDash(goal)}` +
    `（あと ${yenOrDash(Math.abs(diff))}${pct != null ? ` / ${diff >= 0 ? '+' : '-'}${Math.abs(pct)}%` : ''}）` +
    `・残り期間 ${span}。この差を全レバーに重み付き配分（設定する注力1〜3つで約6割）して逆算し、注力指標はその担当分を提案値にする（1指標で全額を埋めない）`
  );
}

export function buildGoalsConsultSummary({
  kpis,
  selectedMonth,
  kpiHistory,
  mlForecasts,
  draft,
  gapLine,
  identityLine,
  now = new Date(),
}) {
  const incomplete = Boolean(selectedMonth && !isYmComplete(selectedMonth, now));
  const last = incomplete ? findLastCompleteHistoryRow(kpiHistory, now) : { ym: null, row: null };
  const head = incomplete
    ? `${buildKpiSummary(kpis, selectedMonth)}（途中月・月未完了）`
    : buildKpiSummary(kpis, selectedMonth);
  const hist = buildSalesHistorySummary(
    kpiHistory,
    18,
    String(draft?.focusKeys || '')
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean)
  );
  const fc = buildDossierForecastSection(mlForecasts, { kpis, selectedMonth, kpiHistory, now });
  const d = draft || {};
  const gapOpts =
    last.row && last.ym
      ? { baselineKpis: last.row, baselineLabel: `直近完了月(${last.ym})売上` }
      : {};
  const draftLine = [
    '目標ドラフト:',
    d.salesGoal != null ? `単月売上ゴール ¥${Math.round(d.salesGoal).toLocaleString('ja-JP')}` : null,
    d.subKgiLabel ? `追加KGI ${d.subKgiLabel}（key=${d.subKgiKey}・注力に選ばない）` : '追加KGI なし',
    d.planMonths != null ? `期間 ${d.planMonths}ヶ月` : null,
    d.focusKeys ? `注力キー ${d.focusKeys}` : null,
    d.focusLabel ? `注力 ${d.focusLabel}` : null,
    d.focusCurrent != null ? `現状実績 ${d.focusCurrent}` : null,
    d.focusTarget != null ? `改善目標案 ${d.focusTarget}` : null,
    d.proposalWhy ? `算出根拠: ${d.proposalWhy}` : null,
  ]
    .filter(Boolean)
    .join(' / ');
  return [
    head,
    buildIncompleteMonthSection(kpiHistory, selectedMonth, now),
    buildGoalGapLine(kpis, d, gapOpts),
    hist,
    buildFocusRangeSection(kpiHistory),
    fc,
    gapLine || null,
    identityLine || null,
    draftLine,
    'モード: 目標相談。注力を明示し「あなたはこのままだとこう」を予測で示す。' +
      'up指標の提案値は現状実績より大きく、down（来店周期など）は小さく。' +
      '実績以下（同じ含む）の改善目標は禁止。スナップショットに無い数値は捏造しない。' +
      (incomplete
        ? '当月は未完了。逆算の基準は直近完了月。当月の途中値を月次実力として語るな。'
        : '') +
      'GOAL_PROPOSAL には提案する枠の key（focusKey / subFocusKey / thirdFocusKey）を入れる。' +
      'KGIの指標（売上・追加KGI）は注力にしない。',
  ]
    .filter(Boolean)
    .join('\n');
}

/** Last non-null NRR from monthly history (prefer selected month, else newest). */
export function resolveNewReturnFromHistory(kpiHistory, selectedMonth) {
  if (!Array.isArray(kpiHistory) || kpiHistory.length === 0) return null;
  const read = (row) => {
    const v = row?.new_return_rate ?? row?.new_customer_return_rate;
    return v != null && !Number.isNaN(Number(v)) ? Number(v) : null;
  };
  if (selectedMonth) {
    const hit = kpiHistory.find((r) => r.month === selectedMonth || r.ym === selectedMonth);
    const v = hit ? read(hit) : null;
    if (v != null) return { value: v, month: selectedMonth, fromHistory: true };
  }
  for (let i = kpiHistory.length - 1; i >= 0; i -= 1) {
    const v = read(kpiHistory[i]);
    if (v != null) {
      return {
        value: v,
        month: kpiHistory[i].month || kpiHistory[i].ym || null,
        fromHistory: true,
      };
    }
  }
  return null;
}

function yenJa(v) {
  return typeof v === 'number' && !Number.isNaN(v)
    ? `¥${Math.round(v).toLocaleString('ja-JP')}`
    : '未設定';
}

function formatFocusTarget(entry, target) {
  if (target == null || Number.isNaN(Number(target))) return '未設定';
  const n = Number(target);
  const unit = entry?.unit;
  if (unit === 'currency') return yenJa(n);
  if (unit === 'percent') return `${n}%`;
  if (unit === 'count') return `${Math.round(n)}名`;
  if (unit === 'days') return `${Math.round(n)}日`;
  return String(n);
}

function formatFocusActual(entry, actual) {
  if (actual == null || Number.isNaN(Number(actual))) return '未取得';
  const n = Number(actual);
  const unit = entry?.unit;
  if (unit === 'currency') return yenJa(n);
  if (unit === 'percent') return `${n}%`;
  if (unit === 'count') return `${Math.round(n)}名`;
  if (unit === 'days') return `${Math.round(n)}日`;
  return String(n);
}

function readFocusActualLocal(focusKey, kpis, kpiHistory, selectedMonth) {
  if (!focusKey || !KPI_BY_FOCUS_KEY[focusKey]) return null;
  if (focusKey === 'newReturn') {
    const fromKpi = readKpiValue(KPI_BY_FOCUS_KEY.newReturn, kpis);
    if (fromKpi != null) return fromKpi;
    return resolveNewReturnFromHistory(kpiHistory, selectedMonth)?.value ?? null;
  }
  if (focusKey === 'unitPrice') {
    const u = deriveUnitPrice(kpis);
    if (u != null) return u;
    if (!Array.isArray(kpiHistory)) return null;
    const row =
      kpiHistory.find((r) => r.month === selectedMonth || r.ym === selectedMonth) ||
      kpiHistory[kpiHistory.length - 1];
    return row ? deriveUnitPrice(row) : null;
  }
  if (focusKey === 'customerCount') {
    return deriveCustomerCount(kpis);
  }
  return readKpiValue(KPI_BY_FOCUS_KEY[focusKey], kpis);
}

/** 追加KGI keys (mirror of kpiFocusCatalog SUB_KGI_KEYS — that module imports this one). */
const SUB_KGI_KEYS = ['nomination', 'nominationCustomers'];

/**
 * Saved 注力①②③ + 追加KGI, same rules as kpiFocusCatalog.resolveFocusSelection (which cannot
 * be imported here without a cycle): a slot repeating an earlier one, or equal to the 追加KGI, is null.
 */
function resolveSavedFocusKeys(goals) {
  const subKgiKey = SUB_KGI_KEYS.includes(goals?.subKgiKey) ? goals.subKgiKey : null;
  const taken = [];
  const pick = (raw) => {
    const key = raw && KPI_BY_FOCUS_KEY[raw] ? raw : null;
    if (!key || taken.includes(key) || key === subKgiKey) return null;
    taken.push(key);
    return key;
  };
  const focusKey = pick(goals?.focusKey);
  const subFocusKey = pick(goals?.subFocusKey);
  const thirdFocusKey = pick(goals?.thirdFocusKey);
  return { focusKey, subFocusKey, thirdFocusKey, subKgiKey };
}

/** 「追加KGI: 指名率（key=nomination） 現状 67.7% / 目標 80%」 or 「追加KGI: なし」. */
function subKgiLine(goals, kpis, kpiHistory, selectedMonth) {
  const { subKgiKey } = resolveSavedFocusKeys(goals);
  if (!subKgiKey) return '追加KGI: なし';
  const entry = KPI_BY_FOCUS_KEY[subKgiKey];
  const actual = readFocusActualLocal(subKgiKey, kpis, kpiHistory, selectedMonth);
  return (
    `追加KGI: ${entry?.label || subKgiKey}（key=${subKgiKey}） 現状 ${formatFocusActual(entry, actual)}` +
    ` / 目標 ${formatFocusTarget(entry, goals?.subKgiTarget)}（KGIなので注力には選ばない）`
  );
}

function focusSlotLine(slotLabel, focusKey, target, kpis, kpiHistory, selectedMonth) {
  if (!focusKey) return `${slotLabel}: 未設定`;
  const entry = KPI_BY_FOCUS_KEY[focusKey];
  const label = entry?.label || focusKey;
  const actual = readFocusActualLocal(focusKey, kpis, kpiHistory, selectedMonth);
  return `${slotLabel}: ${label}（key=${focusKey}） 現状 ${formatFocusActual(entry, actual)} / 目標 ${formatFocusTarget(entry, target)}`;
}

const PLAN_CIRCLED = ['①', '②', '③', '④', '⑤'];
/** Max rows in a 行動プラン — same as the manual editor and BE MAX_PLAN_ACTIONS. */
export const MAX_PLAN_ACTIONS = 5;

/**
 * 「毎日の行動プラン: ①次回予約を提案（指標=nomination） ②…」— the model must echo exact titles
 * (ACTION_REPLACE) and sees which 指標 each action is for. Untagged (legacy) rows print the title only.
 */
export function formatPlanLine(actions) {
  const rows = (Array.isArray(actions) ? actions : [])
    .filter((a) => a?.name && String(a.name).trim())
    .slice(0, MAX_PLAN_ACTIONS)
    .map((a, i) => {
      // Same rule as BE parse_action_focus_key: a known key, never 売上 (the KGI has no action)
      const tagged = a.focusKey && a.focusKey !== 'totalSales' && KPI_BY_FOCUS_KEY[a.focusKey];
      const tag = tagged ? `（指標=${a.focusKey}）` : '';
      return `${PLAN_CIRCLED[i]}${String(a.name).trim()}${tag}`;
    });
  return rows.length ? `毎日の行動プラン: ${rows.join(' ')}` : '毎日の行動プラン: 未設定';
}

/**
 * 「行動プランの指標（重要な順）: 指名率(nomination) → 客単価(unitPrice)」— 追加KGI first, then
 * 注力①②③ (owner 2026-10-16). The 【改善相談】 rules size and tag the plan from this line.
 */
function actionTargetsLine(keys) {
  const list = [keys.subKgiKey, keys.focusKey, keys.subFocusKey, keys.thirdFocusKey].filter(Boolean);
  if (!list.length) return '行動プランの指標（重要な順）: 未設定';
  return `行動プランの指標（重要な順）: ${list
    .map((k) => `${KPI_BY_FOCUS_KEY[k]?.label || k}(${k})`)
    .join(' → ')}`;
}

/**
 * Saved goals → short 目標・注力 block for AI-tab dossier (not GoalEdit draft/GAP).
 */
export function formatGoalsForChat(goals, kpis, kpiHistory = null, selectedMonth = null) {
  const g = goals && typeof goals === 'object' ? goals : null;
  const sales =
    g?.salesTarget != null && !Number.isNaN(Number(g.salesTarget))
      ? Number(g.salesTarget)
      : g?.finalSales != null && !Number.isNaN(Number(g.finalSales))
        ? Number(g.finalSales)
        : null;

  const periodParts = [];
  if (g?.goalDate) periodParts.push(`期限 ${String(g.goalDate).slice(0, 7)}`);
  if (g?.planLength != null && Number.isFinite(Number(g.planLength))) {
    periodParts.push(`期間 ${Number(g.planLength)}ヶ月`);
  }
  const period = periodParts.length ? periodParts.join(' / ') : '期間 未設定';

  // ① is required, so an empty ① is worth saying. ②③ are optional: an unset one is simply left
  // out — a 「注力②: 未設定」line reads to the model as something that must be filled.
  const savedKeys = resolveSavedFocusKeys(g);
  const { focusKey, subFocusKey, thirdFocusKey } = savedKeys;
  const focusLines = [
    focusSlotLine('注力①', focusKey, g?.focusTarget, kpis, kpiHistory, selectedMonth),
    subFocusKey ? focusSlotLine('注力②', subFocusKey, g?.subFocusTarget, kpis, kpiHistory, selectedMonth) : null,
    thirdFocusKey ? focusSlotLine('注力③', thirdFocusKey, g?.thirdFocusTarget, kpis, kpiHistory, selectedMonth) : null,
  ].filter(Boolean);

  const planLine = formatPlanLine(g?.actions);

  return [
    '【目標・注力】（保存済み・捏造禁止）',
    `単月売上ゴール: ${yenJa(sales)}`,
    subKgiLine(g, kpis, kpiHistory, selectedMonth),
    period,
    ...focusLines,
    actionTargetsLine(savedKeys),
    planLine,
  ].join('\n');
}

function monthKeyOf(row) {
  return row?.month || row?.ym || '?';
}

function numOrDash(v, suffix = '') {
  return typeof v === 'number' && !Number.isNaN(v) ? `${v}${suffix}` : '—';
}

function yenOrDash(v) {
  return typeof v === 'number' && !Number.isNaN(v)
    ? `¥${Math.round(v).toLocaleString('ja-JP')}`
    : '—';
}

/** Compact multi-month 実績 lines for dossier. */
export function buildDossierHistorySection(kpiHistory, historyLimit = CHAT_DOSSIER_HISTORY_LIMIT) {
  if (!Array.isArray(kpiHistory) || kpiHistory.length === 0) {
    return '【月次実績】未取得';
  }
  const rows = kpiHistory.slice(-Math.max(1, historyLimit || CHAT_DOSSIER_HISTORY_LIMIT));
  const lines = rows.map((r) => {
    const cust = deriveCustomerCount(r);
    const unit = deriveUnitPrice(r);
    const nrr = r.new_return_rate ?? r.new_customer_return_rate;
    // 新規/リピート per month: without them the model cannot decompose a rate into its
    // numerator and denominator for any past month, and hedges with "not disclosed".
    const nw = r.new_customer_count ?? r.new_customers;
    const rp = r.repeat_customer_count ?? r.repeat_customers;
    const split =
      nw != null || rp != null ? `（新規${numOrDash(nw)}・リピート${numOrDash(rp)}）` : '';
    return (
      `${monthKeyOf(r)}: 売上${yenOrDash(r.total_sales)} / 客数${numOrDash(cust, '名')}${split}` +
      ` / 指名${numOrDash(r.nomination_rate, '%')} / 指名客${numOrDash(r.nominated_customers, '名')}` +
      ` / 客単価${yenOrDash(unit)}` +
      ` / 店販${yenOrDash(r.retail_sales)} / 技術売上${yenOrDash(deriveTechSales(r))}` +
      ` / 新規再来${numOrDash(nrr, '%')}`
    );
  });
  return `【月次実績】（直近${rows.length}ヶ月）\n` + lines.join('\n');
}

/**
 * 店舗平均 for the selected month — the only cross-stylist comparison the chat may make.
 *
 * Payload comes from GET /dashboard/kpis/store-average (already loaded by DashboardContext),
 * which is a true per-stylist average: ratio-of-sums for rates, SUM/stylist_count for
 * additive KPIs. With one stylist on staff the "average" IS that stylist, so we say so
 * instead of handing the model a number it would phrase as a comparison.
 */
export function buildDossierStoreAverageSection(storeAverageKpis) {
  const k = storeAverageKpis;
  if (!k || typeof k !== 'object') return '【店舗平均】未取得';
  const n = typeof k.stylist_count === 'number' ? k.stylist_count : null;
  if (n != null && n <= 1) return '【店舗平均】比較不可（在籍スタイリスト1名のため本人と同値）';
  const cust = deriveCustomerCount(k);
  const unit = deriveUnitPrice(k);
  const nrr = k.new_return_rate ?? k.new_customer_return_rate;
  return (
    `【店舗平均】（同月・スタイリスト${numOrDash(n, '名')}の平均）\n` +
    `売上${yenOrDash(k.total_sales)} / 客数${numOrDash(cust, '名')}` +
    ` / 指名${numOrDash(k.nomination_rate, '%')} / 客単価${yenOrDash(unit)}` +
    ` / 店販${yenOrDash(k.retail_sales)} / 新規再来${numOrDash(nrr, '%')}`
  );
}

/** Model-vs-pace disagreement thresholds (task 2026-10-26 C): beyond these the row says 要注意. */
export const FORECAST_PACE_RATIO = 1.3;
export const FORECAST_LAST_MONTH_RATIO = 1.5;

/**
 * 当月のペース着地 = 当月実績 ÷ 経過日数 × 月の日数 (calendar days; business days are unknown here).
 * POS data lands the next morning, so the actual covers the days BEFORE today: elapsed = day − 1.
 * Only for the JST current month, from the 2nd on; null otherwise.
 */
export function paceLanding(kpis, selectedMonth, now = new Date()) {
  const actual = typeof kpis?.total_sales === 'number' ? kpis.total_sales : null;
  if (actual == null || !selectedMonth || jstMonth(now) !== selectedMonth) return null;
  const today = jstToday(now);
  const day = Number(today.slice(8, 10));
  const elapsed = day - 1;
  const daysInMonth = new Date(Number(selectedMonth.slice(0, 4)), Number(selectedMonth.slice(5, 7)), 0).getDate();
  if (elapsed < 1 || !daysInMonth) return null;
  return { landing: Math.round((actual / elapsed) * daysInMonth), day, elapsed, daysInMonth, actual };
}

/**
 * The simplest baseline next to the model number, and a flag when they disagree. The model
 * extrapolation reaches the dossier unfiltered (quality gate off by decision), so the prompt
 * must never see the model number alone for the open month (渡邊さん 10/23: model ¥1.84M vs
 * ¥795K actual on the 23rd).
 */
export function forecastPaceNote(row, { kpis, selectedMonth, kpiHistory, now = new Date() } = {}) {
  if (!row || monthKeyOf(row) !== selectedMonth) return '';
  const pace = paceLanding(kpis, selectedMonth, now);
  const model = typeof row.total_sales === 'number' ? row.total_sales : null;
  if (!pace || model == null) return '';
  const prevYm = previousYm(selectedMonth);
  const prevRow = Array.isArray(kpiHistory) ? kpiHistory.find((h) => monthKeyOf(h) === prevYm) : null;
  const prevSales = typeof prevRow?.total_sales === 'number' ? prevRow.total_sales : null;
  const caution =
    (pace.landing > 0 && model / pace.landing > FORECAST_PACE_RATIO) ||
    (prevSales != null && prevSales > 0 && model / prevSales > FORECAST_LAST_MONTH_RATIO);
  return (
    ` / ペース着地${yenOrDash(pace.landing)}（${pace.day}日時点 ${yenOrDash(pace.actual)} ÷ 経過${pace.elapsed}日 × ${pace.daysInMonth}日）` +
    (caution ? ' / 要注意: モデル予測は現状ペースを大きく上回る。計画の基準はペース着地' : '')
  );
}

/** Compact 予測 lines from ml_forecast rows already on Home. `opts` adds the pace baseline to the open month. */
export function buildDossierForecastSection(mlForecasts, opts = {}) {
  if (!Array.isArray(mlForecasts) || mlForecasts.length === 0) {
    return '【予測】未取得';
  }
  const rows = [...mlForecasts].sort((a, b) => String(monthKeyOf(a)).localeCompare(String(monthKeyOf(b))));
  const lines = rows.map((r) => {
    const nrr = r.new_return_rate ?? r.new_customer_return_rate;
    const nrrS = typeof nrr === 'number' ? `${nrr}%` : '予測なし';
    return (
      `${monthKeyOf(r)}: モデル予測 売上${yenOrDash(r.total_sales)} / 店販${yenOrDash(r.retail_sales)}` +
      ` / 指名${numOrDash(r.nomination_rate, '%')} / 指名客${numOrDash(r.nominated_customers, '名')}` +
      ` / 新規${numOrDash(r.new_customer_count)}` +
      ` / リピ${numOrDash(r.repeat_customer_count)} / NRR ${nrrS}` +
      forecastPaceNote(r, opts)
    );
  });
  return (
    `【予測】（${rows.length}ヶ月・モデル予測＝本人の過去実績から機械的に計算した見込み。` +
    '直近の水準に、去年を含む月ごとの季節パターンを掛けたもの。予約・目標は見ていない・NRR無ければ予測なし）\n' +
    lines.join('\n')
  );
}

/**
 * Meta for AI-tab 読込 chip / banner.
 * @returns {{ selectedMonth: string, historyFirst: string|null, historyLast: string|null, historyCount: number, forecastCount: number, hasGoals: boolean, chip: string }}
 */
export function getChatDossierMeta({
  selectedMonth,
  kpiHistory,
  mlForecasts,
  goals,
  historyLimit = CHAT_DOSSIER_HISTORY_LIMIT,
} = {}) {
  const hist = Array.isArray(kpiHistory) ? kpiHistory : [];
  const sliced = hist.slice(-Math.max(1, historyLimit || CHAT_DOSSIER_HISTORY_LIMIT));
  const historyFirst = sliced.length ? monthKeyOf(sliced[0]) : null;
  const historyLast = sliced.length ? monthKeyOf(sliced[sliced.length - 1]) : null;
  const forecastCount = Array.isArray(mlForecasts) ? mlForecasts.length : 0;
  const sales =
    goals?.salesTarget != null || goals?.finalSales != null;
  const { focusKey, subFocusKey, thirdFocusKey, subKgiKey } = resolveSavedFocusKeys(goals);
  const hasGoals = Boolean(sales || focusKey || subFocusKey || thirdFocusKey || subKgiKey);
  const histPart =
    historyFirst && historyLast
      ? `実績 ${historyFirst}〜${historyLast} (${sliced.length}ヶ月)`
      : '実績 未取得';
  const chip = `読込: 対象月 ${selectedMonth || '—'} / ${histPart} / 予測 ${forecastCount}ヶ月 / 目標 ${hasGoals ? 'あり' : '未設定'}`;
  return {
    selectedMonth: selectedMonth || '',
    historyFirst,
    historyLast,
    historyCount: sliced.length,
    forecastCount,
    hasGoals,
    chip,
  };
}

/** Refresh or append 毎日の行動プラン line without duplicating the block. */
export function mergePlanIntoKpiSummary(base, planLine) {
  const b = (base || '').trim();
  const p = (planLine || '').trim();
  if (!p) return b || undefined;
  if (!b) return p;
  if (/毎日の行動プラン:/.test(b)) {
    return b.replace(/毎日の行動プラン:[^\n]*/u, p).trim();
  }
  return `${b}\n${p}`;
}

/**
 * AI-tab dossier → existing `kpi_summary` body (no new BE field).
 */
export function buildChatDossier({
  kpis,
  selectedMonth,
  kpiHistory,
  mlForecasts,
  goals = null,
  planLine = null,
  storeAverageKpis = null,
  historyLimit = CHAT_DOSSIER_HISTORY_LIMIT,
  now = new Date(),
} = {}) {
  const meta = getChatDossierMeta({
    selectedMonth,
    kpiHistory,
    mlForecasts,
    goals,
    historyLimit,
  });
  const monthBlock = buildKpiSummary(kpis, selectedMonth);
  const goalsBlock = formatGoalsForChat(goals, kpis, kpiHistory, selectedMonth);
  let goalsOut = goalsBlock;
  const p = (planLine || '').trim();
  if (p) {
    if (/毎日の行動プラン:/.test(goalsOut)) {
      goalsOut = goalsOut.replace(/毎日の行動プラン:[^\n]*/u, p);
    } else {
      goalsOut = `${goalsOut}\n${p}`;
    }
  }
  const parts = [
    `【読込】${meta.chip.replace(/^読込:\s*/, '')}`,
    // The month line shows 新規N and 新規再来率 side by side; without this the model links
    // them and writes "of this month's N new customers none returned" — impossible by
    // construction (the rate is the 3-months-ago cohort, dates shifted +3 in SQL).
    '【指標の定義】新規再来率 = 3ヶ月前に新規来店したお客様のうち90日以内に再来した割合' +
      '（例: 7月の値は4月新規が対象）。当月の新規客数とは別の集団。',
    monthBlock ? `【当月実績】\n${monthBlock}` : '【当月実績】未取得',
    buildIncompleteMonthSection(kpiHistory, selectedMonth, now),
    buildDossierStoreAverageSection(storeAverageKpis),
    goalsOut,
    buildDossierHistorySection(kpiHistory, historyLimit),
    buildDossierForecastSection(mlForecasts, { kpis, selectedMonth, kpiHistory, now }),
  ];
  return parts.filter(Boolean).join('\n');
}
